import * as Dialog from '@radix-ui/react-dialog'
import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Modal — 토스 스타일 large modal 기본 원소.
 * - 백드롭: rgba(0,0,0,0.4) + backdrop-filter blur(4px), fade-in 0.2s
 * - 모달: max-width 720(기본), shadow-lg, radius-xl, scale 0.96→1 0.18s cubic-bezier(0.2,0.8,0.2,1)
 * - ESC + 백드롭 클릭 + Tab 트랩 = Radix가 처리
 *
 * 모달 표면(헤더·본문·푸터)은 컴포저블하게 children으로 받는다.
 */
export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  children?: ReactNode
  footer?: ReactNode
  hideClose?: boolean
}

const SIZE = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
} as const

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = 'lg',
  children,
  footer,
  hideClose,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/40 backdrop-blur-[4px]',
            'data-[state=open]:animate-[vibe-backdrop-in_0.2s_ease-out]',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100%-32px)]',
            SIZE[size],
            'bg-surface rounded-xl shadow-lg',
            'data-[state=open]:animate-[vibe-modal-in_0.18s_cubic-bezier(0.2,0.8,0.2,1)]',
            'focus:outline-none',
            'max-h-[90vh] overflow-hidden flex flex-col',
          )}
        >
          <header className="flex items-start justify-between gap-3 px-6 pt-6 pb-3">
            <div className="min-w-0">
              <Dialog.Title className="text-[20px] font-extrabold tracking-tight text-text">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-[13px] text-text-2 leading-relaxed">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {!hideClose && (
              <Dialog.Close
                className="rounded-md p-1.5 text-text-3 hover:bg-surface-alt hover:text-text transition-colors"
                aria-label="닫기"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Dialog.Close>
            )}
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

          {footer && (
            <footer className="px-6 py-4 border-t border-border bg-surface-alt/50">
              {footer}
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
